import { useEffect, useRef, useState } from "react";
import { Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useBriefingCrewNames, useBriefingFlights } from "@/hooks/useBriefingFlights";
import { FlightLegEditor } from "@/components/briefing/FlightLegEditor";
import { FLIGHT_DIRECTION_LABEL, FLIGHT_DIRECTIONS, type FlightDirection } from "@produce/shared";
import {
  copyPassengersFrom,
  draftsForDirection,
  emptyFlightForm,
  formToLegInput,
  formToPassengerInput,
  passengersForDirection,
  reconcileDrafts,
  type FlightDraft,
} from "@/lib/flightForm";

// The flights popup.
//
// It lives in a modal so the general briefing page stays readable: this
// is a lot of fields, and only some productions need them at all.
//
// One tab per direction. A direction can hold several legs — that is how
// a flight with a stopover is entered (AMS→MAD, MAD→LIS), and each leg
// carries its own passengers so a group that splits up mid-journey is
// still expressible.
//
// ALL draft state lives here rather than in the leg editors. That is
// what makes filling in Outbound, switching straight to Return and
// closing keep everything: switching tabs and closing both flush the
// pending drafts. A producer entering two flights in one sitting should
// never have to think about a save button between them.

export function FlightsDialog({
  open,
  onOpenChange,
  briefingId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  briefingId: string;
}) {
  const { data, saveLeg, deleteLeg, invalidate } = useBriefingFlights(open ? briefingId : null);
  const [drafts, setDrafts] = useState<FlightDraft[]>([]);
  const [tab, setTab] = useState<FlightDirection>("outbound");
  const [saving, setSaving] = useState(false);

  // A save queued behind another one must work from the CURRENT drafts,
  // not from the state captured when it was requested. Plain render-time
  // mirror — no hand-written updates, because every leg now carries a
  // client-assigned id, so re-saving a draft the mirror still shows as
  // dirty just upserts the same row again.
  const draftsRef = useRef(drafts);
  draftsRef.current = drafts;

  // Serializes saves. Radix fires a tab change on both focus and click,
  // so one deliberate click could start two saves a frame apart.
  const flushChainRef = useRef<Promise<boolean>>(Promise.resolve(true));

  useEffect(() => {
    if (!open) return;
    setDrafts((current) => reconcileDrafts(current, data.legs, data.passengers));
  }, [open, data]);

  // Reset when the dialog closes so the next open starts from the
  // server, not from a stale session.
  useEffect(() => {
    if (open) return;
    setDrafts([]);
    setTab("outbound");
  }, [open]);

  const { data: crew = [] } = useBriefingCrewNames(briefingId, open);

  const updateDraft = (id: string, patch: Partial<FlightDraft>) =>
    setDrafts((current) => current.map((d) => (d.id === id ? { ...d, ...patch, dirty: true } : d)));

  /**
   * Nine times out of ten the same people fly home. Offered on return
   * legs only, and only once somebody is actually on the outbound —
   * a button that would do nothing is worse than no button.
   */
  const outboundPassengers = passengersForDirection(drafts, "outbound");

  const copyOutboundPassengers = (id: string) =>
    setDrafts((current) =>
      current.map((d) =>
        d.id === id
          ? { ...d, dirty: true, passengers: copyPassengersFrom(outboundPassengers, d.passengers) }
          : d,
      ),
    );

  const addLeg = (direction: FlightDirection) => {
    // max + 1, not count: after deleting a middle leg the orders can be
    // [0, 2], and a count-based order would repeat the 2.
    const legOrder = draftsForDirection(drafts, direction).reduce(
      (max, d) => Math.max(max, d.values.legOrder + 1),
      0,
    );
    setDrafts((current) => [
      ...current,
      {
        id: crypto.randomUUID(),
        dirty: true,
        values: emptyFlightForm(direction, legOrder),
        passengers: [],
      },
    ]);
  };

  /**
   * Write every touched draft. Returns false when something failed, so
   * callers can keep the dialog open on the producer's work instead of
   * closing over an error.
   *
   * Sequential: each save re-reads that leg's passenger rows to diff
   * them, and the crew push trigger fires per write — concurrency buys
   * nothing here and makes ordering murky.
   */
  const runFlush = async (): Promise<boolean> => {
    const pending = draftsRef.current.filter((d) => d.dirty);
    if (pending.length === 0) return true;

    setSaving(true);
    try {
      for (const draft of pending) {
        await saveLeg.mutateAsync({
          legId: draft.id,
          leg: formToLegInput(draft.values),
          passengers: draft.passengers.map(formToPassengerInput),
        });
        setDrafts((current) =>
          current.map((d) => (d.id === draft.id ? { ...d, dirty: false } : d)),
        );
      }
      // Once, after the batch — invalidating per leg made every save
      // block on a refetch the next save immediately superseded.
      invalidate();
      return true;
    } catch {
      toast.error("Could not save the flights.");
      return false;
    } finally {
      setSaving(false);
    }
  };

  /** Queue a save behind any save already running. */
  const flush = (): Promise<boolean> => {
    const queued = flushChainRef.current.then(runFlush);
    flushChainRef.current = queued;
    return queued;
  };

  const handleTabChange = async (value: string) => {
    // Radix reports a change on focus AND on click; re-saving for a tab
    // that is already open is pure waste.
    if (value === tab) return;
    // Save what is on screen before leaving it. A failed save must not
    // silently move the producer away from work they'd lose.
    if (!(await flush())) return;
    setTab(value as FlightDirection);
  };

  const handleClose = async () => {
    if (!(await flush())) return;
    onOpenChange(false);
  };

  const handleDelete = async (draft: FlightDraft) => {
    try {
      await deleteLeg.mutateAsync(draft.id);
      setDrafts((current) => current.filter((d) => d.id !== draft.id));
      toast.success("Flight removed.");
    } catch {
      toast.error("Could not remove the flight.");
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // The X and the overlay go through the same save path as the
        // button, so there is no way to lose work by closing.
        if (!next) void handleClose();
        else onOpenChange(true);
      }}
    >
      <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Flights</DialogTitle>
          <DialogDescription>
            Add each flight once and tick everyone who is on it. A crew member only ever sees their
            own flight details in the app. Your changes are saved automatically.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={(value) => void handleTabChange(value)}>
          <TabsList className="grid w-full grid-cols-2">
            {FLIGHT_DIRECTIONS.map((direction) => (
              <TabsTrigger key={direction} value={direction}>
                {FLIGHT_DIRECTION_LABEL[direction]}
              </TabsTrigger>
            ))}
          </TabsList>

          {FLIGHT_DIRECTIONS.map((direction) => {
            const legs = draftsForDirection(drafts, direction);

            return (
              <TabsContent key={direction} value={direction} className="space-y-4 pt-4">
                {legs.length === 0 && (
                  <p className="text-sm text-muted-foreground">
                    No {FLIGHT_DIRECTION_LABEL[direction].toLowerCase()} flight added yet.
                  </p>
                )}

                {legs.map((draft, index) => (
                  <div key={draft.id} className="space-y-2">
                    {legs.length > 1 && (
                      <p className="text-sm font-semibold text-muted-foreground">
                        {index === 0 ? "First flight" : `Connecting flight ${index}`}
                      </p>
                    )}
                    <FlightLegEditor
                      briefingId={briefingId}
                      legOrder={draft.values.legOrder}
                      values={draft.values}
                      passengers={draft.passengers}
                      crew={crew}
                      onChange={(values) => updateDraft(draft.id, { values })}
                      onPassengersChange={(passengers) => updateDraft(draft.id, { passengers })}
                      onDelete={() => void handleDelete(draft)}
                      deleting={deleteLeg.isPending}
                      onCopyPassengers={
                        direction === "return" && outboundPassengers.length > 0
                          ? () => copyOutboundPassengers(draft.id)
                          : undefined
                      }
                    />
                  </div>
                ))}

                <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border pt-4">
                  <Button type="button" variant="outline" onClick={() => addLeg(direction)}>
                    <Plus className="mr-2 h-4 w-4" />
                    {legs.length === 0 ? "Add flight" : "Add connecting flight"}
                  </Button>
                  <Button type="button" onClick={() => void handleClose()} disabled={saving}>
                    {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Close and save flights
                  </Button>
                </div>
              </TabsContent>
            );
          })}
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
